import { Component, findComponents, startComponents, stopComponents } from '@gondel/core';
import { GondelJqueryComponent } from '@gondel/plugin-jquery';
import { ListFilter } from '@swisslife/patterns-base/src/molecules/list-filter/js/list-filter';
import '@swisslife/patterns-base/src/molecules/list-filter/js/list-filter';
import ajax from '@swisslife/shared-ui/src/utils/ajax/js/ajax';
import { dispatchCustomEvent, EventNames } from '@swisslife/shared-ui/src/utils/custom-event/js/custom-event';
import addComponentAnimation from '@swisslife/shared-ui/src/utils/animations/js/animations';

export interface IConfiguration {
	filterPrefix: string;
	serviceUri: string;
	serviceUriSuffix: string;
}

enum States {
	LoaderVisible = 'state-a-loader--visible',
	Error = 'state-o-filter--error',
}

enum Selectors {
	Title = '.js-o-filter__title',
	ListFilter = '.js-o-filter__list-filter',
	Loader = '.js-a-loader',
	ListOutlet = '.js-o-filter__outlet',
}

const categoryKey = 'filter';

@Component('Filter')
export class Filter extends GondelJqueryComponent {
	private controllersTitle: any[] = Array();
	private controllersListFilter: any[] = Array();
	private $loader: JQuery<HTMLElement>;
	private $listOutlet: JQuery<HTMLElement>;
	private listFilter: ListFilter;
	private configuration: IConfiguration;
	private defaultCategory: { itemKey: string; itemName: string } = { itemKey: '', itemName: '' };
	private selectedCategory: { itemKey: string; itemName: string } = { itemKey: '', itemName: '' };
	private selectedSubCategory: string = '';

	public start() {
		// add list-filter dependencies and register callback
		const listFilterComponents = findComponents<ListFilter>(this._ctx, 'ListFilter');

		if (listFilterComponents.length > 0) {
			this.listFilter = listFilterComponents[0];
			this.listFilter.registerCallback(this.listFilterClickCallback);
		}

		this.$loader = $(Selectors.Loader, this.$ctx);
		this.$listOutlet = $(Selectors.ListOutlet, this.$ctx);

		this.configuration = {
			filterPrefix: 'id-',
			serviceUri: this.$listOutlet.data('service-uri'),
			serviceUriSuffix: this.$listOutlet.data('service-uri-suffix') || '',
		};

		if (this.listFilter) {
			$(window).on('popstate.filter', this.handleHistoryPopState.bind(this));
		}
	}

	public async sync() {
		if (this.listFilter) {
			this.defaultCategory = this.listFilter.getDefaultCategoryKey();
			this.selectedCategory = this.defaultCategory;

			const initialSubCategory = this.$ctx.data('initial-subcategory') || '';
			this.listFilter.setFilterState(categoryKey, [initialSubCategory]);
		}

		this.controllersTitle = await addComponentAnimation(this.$ctx, Selectors.Title);
		this.controllersListFilter = await addComponentAnimation(this.$ctx, Selectors.ListFilter);
	}

	public stop() {
		if (this.listFilter) {
			this.listFilter.removeCallback(this.listFilterClickCallback);
		}

		for (const controller of this.controllersTitle) {
			controller.destroy(true);
		}
		for (const controller of this.controllersListFilter) {
			controller.destroy(true);
		}

		$(window).off('popstate.filter');
	}

	private generateRequestUrl(): string {
		const urlArray: string[] = [];

		urlArray.push(`${this.configuration.serviceUri}${this.selectedSubCategory}`);
		urlArray.push(this.configuration.serviceUriSuffix);

		return urlArray.join('.');
	}

	private generateUrlFragmentFilterState(): string {
		const urlArray: string[] = [];

		urlArray.push(`${this.configuration.filterPrefix}${this.selectedSubCategory}`); // e.g. 'id-' + '2018'
		urlArray.push(this.configuration.serviceUriSuffix);

		return urlArray.join('.');
	}

	private generateUnfilteredUrl(): string {
		const urlPathParts = document.location.href.split('/');
		const lastSegment = urlPathParts.pop();
		const restSegment = urlPathParts.join('/');
		let urlFragmentBase = '';

		if (typeof lastSegment !== 'undefined') {
			/* the separating string '.id-' occurs once in agreed url pattern ->
			 *  1st part contains page name, 2nd the filter id (including serviceUriSuffix)
			 */
			const partsLastSegment = lastSegment.split(`.${this.configuration.filterPrefix}`);
			const partsLastSegmentBase = partsLastSegment[0];

			if (typeof partsLastSegmentBase !== 'undefined') {
				const partsLastSegmentWithoutSuffix = partsLastSegmentBase.replace(
					`.${this.configuration.serviceUriSuffix}`,
					''
				); // strip out serviceUriSuffix
				urlFragmentBase = partsLastSegmentWithoutSuffix;
			}
		}

		return `${restSegment}/${urlFragmentBase}`;
	}

	private async getResults(updateHistory: boolean) {
		try {
			this.toggleLoadingState(true);

			// fetch but wait for 150 ms minimum (prevent too fast update / ui flickering)
			const fragment = (await Promise.all([ajax<string>(this.generateRequestUrl()), this.wait(150)]))[0];

			// reflect filter state in url
			const urlFragmentFilterState = this.generateUrlFragmentFilterState(); // e.g. 'id-2017.html'
			const urlFragmentUnfiltered = this.generateUnfilteredUrl();

			if (updateHistory) {
				this.updateHistory(document.title, `${urlFragmentUnfiltered}.${urlFragmentFilterState}`);
			}

			stopComponents(this.$listOutlet);
			this.$listOutlet.empty();
			this.$listOutlet.append(fragment);
			this.$ctx.removeClass(States.Error);
			startComponents(this.$listOutlet);
		} catch (error) {
			const statusCode = error.status ? error.status.toString() : 'n/a';
			dispatchCustomEvent(this.$ctx, EventNames.NetworkError, { statusCode });
			this.$listOutlet.empty();
			this.showErrorMessage();
		}

		this.toggleLoadingState(false);
	}

	private handleHistoryPopState(event): void {
		// get original event since jQuery normalizes the event
		const popStateEvent = event.originalEvent;

		if (popStateEvent.state !== null) {
			const eventData = popStateEvent.state;

			this.selectedCategory = eventData.selectedCategory;
			this.selectedSubCategory = eventData.selectedSubCategories;

			this.listFilter.setFilterState(this.selectedCategory.itemKey, [this.selectedSubCategory]);

			// history popState is always Actions.replace because we do push load more as a new history event
			this.getResults(false);
		} else {
			window.location.reload();
		}
	}

	private listFilterClickCallback = (_: string, filterItem: { itemKey: string; itemName: string }): void => {
		this.updateSelectedSubCategory(filterItem.itemKey);
		this.getResults(true);
		dispatchCustomEvent(this.$ctx, EventNames.FilterClick, {
			filterCategory: 'category',
			filterName: filterItem.itemName.trim(),
			filterID: filterItem.itemKey,
		});
	};

	private showErrorMessage(): void {
		this.$ctx.addClass(States.Error);
	}

	private toggleLoadingState(newState: boolean): void {
		if (newState) {
			this.$loader.addClass(States.LoaderVisible);
		} else {
			this.$loader.removeClass(States.LoaderVisible);
		}
	}

	private updateHistory(title: string, url: string): void {
		if (window.history) {
			// update uri and page title
			history.pushState(
				{
					selectedCategory: this.selectedCategory,
					selectedSubCategories: this.selectedSubCategory,
				},
				title,
				url
			);
		}

		// set browser tab title even if it's not supported by pushState
		$(document).prop('title', title);
	}

	private updateSelectedSubCategory(filterValue: string): void {
		this.selectedSubCategory = filterValue;
	}

	private wait(delay: number) {
		return new Promise((receive) => setTimeout(receive, delay));
	}
}
